import {isObjectNullOrEmpty} from "../../Types/objectUtilities";
import {apiConstants} from "../apiConstants";
import {requestTypes} from "./requestTypes";
import {desistLogin} from "../../LocalStorage/storageUtilities";
import {catchError} from "./requestUtilities";
import type {AjaxRequestOptions} from "./ajaxRequestOptions";

interface AjaxRequestObject {
    url?: string;
    type?: string;
    contentType?: string;
    data?: unknown;
    crossDomain?: boolean;
    processData?: boolean | null;
    requestType?: string;
    xhrFields?: Record<string, unknown>;
    xhr?: unknown;
    enctype?: string | null;
    dataType?: string | null;
    cache?: boolean;
    success: (response: Response) => void;
    error: (response: Response) => void;
}

function handleSessionExpired(notificationFactory: { clear: () => void; createWarning: (msg: string) => { notify: () => void } }, message = apiConstants.sessionExpiredMessage): void {
    desistLogin();
    notificationFactory.clear();
    notificationFactory.createWarning(message).notify();
    window.location.href = `${apiConstants.apiRedirectPath}`;
}

export const ajaxRequestWithRetry = (notificationFactory: { clear: () => void; createWarning: (msg: string) => { notify: () => void }; createSuccess: (msg: string) => { notify: () => void }; createErrorsFromXhrObject: (errors: unknown) => { notify: () => void }; createNone: () => { notify: () => void } }, isAuthorizedUrl: string) => {

    function copyPropertyIfExists(source: Record<string, unknown>, target: Record<string, unknown>, propertyName: string, throwErrorIfNull = false): void {
        if (
            !isObjectNullOrEmpty(source) &&
            Object.prototype.hasOwnProperty.call(source, propertyName) &&
            source[propertyName] !== null &&
            source[propertyName] !== undefined
        )
            target[propertyName] = source[propertyName];
        else if (throwErrorIfNull) throw new Error(`Property ${propertyName} is required.`);
    }

    function getAjaxRequestObject(ajaxRequestOptions: AjaxRequestOptions): AjaxRequestObject {
        const ajaxRequestObject = { crossDomain: true } as Record<string, unknown>;

        switch (ajaxRequestOptions.type) {
            case requestTypes.GET:
                ajaxRequestObject.type = "GET";
                break;
            case requestTypes.POST:
                ajaxRequestObject.type = "POST";
                break;
            case requestTypes.PUT:
                ajaxRequestObject.type = "PUT";
                break;
            case requestTypes.DELETE:
                ajaxRequestObject.type = "DELETE";
                break;
            default:
                throw new Error("Need to specify http verb for request.");
        }

        const source = ajaxRequestOptions as unknown as Record<string, unknown>;
        copyPropertyIfExists(source, ajaxRequestObject, "url", true);
        copyPropertyIfExists(source, ajaxRequestObject, "contentType");
        copyPropertyIfExists(source, ajaxRequestObject, "processData");
        copyPropertyIfExists(source, ajaxRequestObject, "requestType");
        copyPropertyIfExists(source, ajaxRequestObject, "xhrFields");
        copyPropertyIfExists(source, ajaxRequestObject, "xhr");
        copyPropertyIfExists(source, ajaxRequestObject, "data");
        copyPropertyIfExists(source, ajaxRequestObject, "enctype");
        copyPropertyIfExists(source, ajaxRequestObject, "dataType");
        copyPropertyIfExists(source, ajaxRequestObject, "cache");

        return ajaxRequestObject as unknown as AjaxRequestObject;
    }

    async function readDataFromResponse(response: Response, ajaxRequestOptions: AjaxRequestOptions): Promise<unknown> {
        if (response.status === 204) return null;

        const responseText = await response.text();
        if (!responseText || responseText.trim().length === 0) return null;

        if (ajaxRequestOptions.dataType === "text") return responseText;

        const contentType = (response.headers.get("content-type") || "").toLowerCase();
        const isJsonResponse =
            contentType.includes("application/json") ||
            contentType.includes("+json");

        if (isJsonResponse) {
            return JSON.parse(responseText);
        }

        return responseText;
    }

    function configureDefaultSuccessCallback(ajaxRequestObject: AjaxRequestObject, ajaxRequestOptions: AjaxRequestOptions, resolve: (value: unknown) => void): AjaxRequestObject {
        if (typeof ajaxRequestOptions.successCallback !== "function") {
            ajaxRequestObject.success = async function (response: Response) {
                if (ajaxRequestOptions.successMessage) {
                    notificationFactory.createSuccess(ajaxRequestOptions.successMessage).notify();
                }

                const data = await readDataFromResponse(response, ajaxRequestOptions);
                resolve(data);
            };
        } else {
            ajaxRequestObject.success = async function (response: Response) {
                const data = await readDataFromResponse(response, ajaxRequestOptions);
                ajaxRequestOptions.successCallback!(data, resolve, notificationFactory);
            };
        }
        return ajaxRequestObject;
    }

    function configureDefaultErrorCallback(ajaxRequestObject: AjaxRequestObject, ajaxRequestOptions: AjaxRequestOptions, reject: (reason?: unknown) => void): AjaxRequestObject {
        if (typeof ajaxRequestOptions.errorCallback !== "function") {
            ajaxRequestObject.error = function (errors: Response) {

                const errorMessageNotification = (errorMessages: unknown) => {
                    const notifier = ajaxRequestOptions.notifyOnError
                        ? notificationFactory.createErrorsFromXhrObject(errorMessages)
                        : notificationFactory.createNone();
                    notifier.notify();
                }

                if (typeof errors.text === "function") {
                    errors.text().then(errorMessageNotification);
                } else {
                    errorMessageNotification(errors);
                }

                reject(errors);
            };
        } else {
            ajaxRequestObject.error = function (error: Response) {
                ajaxRequestOptions.errorCallback!(error, reject, notificationFactory);
            };
        }
        return ajaxRequestObject;
    }

    function configure401and404ErrorCallback(ajaxRequestObject: AjaxRequestObject, ajaxRequestOptions: AjaxRequestOptions, reject: (reason?: unknown) => void): AjaxRequestObject {
        const originalRequestObject = {} as AjaxRequestObject;
        Object.assign(originalRequestObject, ajaxRequestObject);
        
        ajaxRequestObject.error = function (response: Response) {
            if (response.status === 401) {
                handleSessionExpired(notificationFactory);
                reject(response);
                return;
            }
            
            if (response.status === 404) {
                sendRequest({
                    url: isAuthorizedUrl,
                    contentType: apiConstants.contentType.JSON,
                    type: "GET",
                    success: function () {
                        originalRequestObject.error(response);
                    },
                    error: function () {
                        handleSessionExpired(notificationFactory);
                    },
                } as unknown as AjaxRequestObject, reject).then(() => {});
            } else {
                originalRequestObject.error(response);
            }
        }
        return ajaxRequestObject;
    }

    async function sendRequest(ajaxRequestOptions: AjaxRequestObject, reject: (reason?: unknown) => void): Promise<void> {
        try {
            let response = await fetch(ajaxRequestOptions.url!, {
                method: ajaxRequestOptions.type,
                headers: {
                    'Content-Type': ajaxRequestOptions.contentType || 'application/json',
                },
                body: ajaxRequestOptions.type !== 'GET' && ajaxRequestOptions.data ? (typeof ajaxRequestOptions.data === 'object' ? JSON.stringify(ajaxRequestOptions.data) : ajaxRequestOptions.data as BodyInit) : null,
                credentials: 'include'

            });

            if (!response.ok) {
                ajaxRequestOptions.error(response);
            } else {
                ajaxRequestOptions.success(response);
            }

        } catch (error) {
            catchError(error);
            reject(error);
            throw error;
        }
    }

    function send(ajaxRequestOptions: AjaxRequestOptions): Promise<unknown> {
        return new Promise((resolve, reject) => {
            let ajaxRequestObject = getAjaxRequestObject(ajaxRequestOptions);

            ajaxRequestObject = configureDefaultSuccessCallback(ajaxRequestObject, ajaxRequestOptions, resolve);
            ajaxRequestObject = configureDefaultErrorCallback(ajaxRequestObject, ajaxRequestOptions, reject);
            ajaxRequestObject = configure401and404ErrorCallback(ajaxRequestObject, ajaxRequestOptions, reject);

            sendRequest(ajaxRequestObject, reject).then();
        });

    }

    return {send};
};
